import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, Paper, TextField, Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

import OutilService from "../../../services/smq/OutilService";
import { fieldSx, OUTIL_COLORS } from "./OutilContainer.styles";

const MAX_FILE_SIZE = 20 * 1024 * 1024;
const EMPTY_FORM = {
  action: "", attente: "", date_debut: "", date_fin: "", deroulement: "", nom_partie: "",
  objectif: "", opportunite: "", processus: "", risque: "", suivi_oi3: "",
};
const fileName = (path) => (String(path || "").split(/[\\/]/).pop() || "").replace(/^[a-f0-9]{24}__/, "") || "Pièce jointe";
const rowForm = (row) => ({
  action: row?.action || "", attente: row?.attente || "",
  date_debut: row?.date_debut ? String(row.date_debut).slice(0, 10) : "",
  date_fin: row?.date_fin ? String(row.date_fin).slice(0, 10) : "",
  deroulement: row?.deroulement || "", nom_partie: row?.nom_partie || "",
  objectif: row?.objectif || "", opportunite: row?.opportunite || "", processus: row?.processus || "",
  risque: row?.risque || "", suivi_oi3: row?.suivi_oi3 || "",
});

const Section = ({ children, title }) => (
  <Paper elevation={0} sx={{ border: `1px solid ${OUTIL_COLORS.border}`, borderRadius: 1.5, overflow: "hidden" }}>
    <Box sx={{ bgcolor: "#f3f7fa", borderBottom: `1px solid ${OUTIL_COLORS.border}`, px: 1.5, py: 1 }}>
      <Typography color={OUTIL_COLORS.navy} fontSize={11} fontWeight={800}>{title}</Typography>
    </Box>
    <Box sx={{ p: 1.5 }}>{children}</Box>
  </Paper>
);

const downloadBlob = (blob, name) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const PartiesInteresseesDialog = ({ mode, onClose, onSaved, open, outilEntree, processus, row }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const inputRef = useRef(null);
  const isCreate = mode === "create";
  const isView = mode === "view";
  const isValidated = row?.statut === "Validé";

  useEffect(() => {
    if (!open) return;
    setForm(isCreate ? EMPTY_FORM : rowForm(row));
    setErrors({});
    setFile(null);
    setSaving(false);
  }, [isCreate, open, row]);

  const change = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setErrors((current) => ({ ...current, [field]: "", submit: "" }));
  };

  const selectFile = (event) => {
    const selected = event.target.files?.[0] || null;
    event.target.value = "";
    if (selected && selected.size > MAX_FILE_SIZE) {
      setFile(null);
      setErrors((current) => ({ ...current, file: "La pièce jointe ne doit pas dépasser 20 Mo." }));
      return;
    }
    setFile(selected);
    setErrors((current) => ({ ...current, file: "" }));
  };

  const validate = () => {
    const next = {};
    if (!String(form.nom_partie).trim()) next.nom_partie = "Nom obligatoire.";
    if (form.suivi_oi3.length > 50) next.suivi_oi3 = "50 caractères maximum.";
    if (form.date_debut && form.date_fin && form.date_fin < form.date_debut) next.date_fin = "La date de fin doit être supérieure ou égale au début.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (submissionAction) => {
    if (saving || !outilEntree?.outil_entree_id || !validate()) return;
    setSaving(true);
    try {
      const data = new FormData();
      data.append("outil_entree_id", outilEntree.outil_entree_id);
      data.append("processus_id", processus.processus_id);
      data.append("submission_action", submissionAction);
      Object.entries(form).forEach(([key, value]) => data.append(key, value ?? ""));
      if (file) data.append("file", file);
      const response = isCreate
        ? await OutilService.createPartie(data)
        : await OutilService.updatePartie(row.partie_interessee_id, data);
      await onSaved(response.message || "Partie intéressée sauvegardée.");
    } catch (error) {
      setErrors((current) => ({ ...current, submit: error.message }));
    } finally {
      setSaving(false);
    }
  };

  const download = async () => {
    if (!row?.partie_interessee_id || downloading) return;
    setDownloading(true);
    try {
      const response = await OutilService.downloadPartieFile(row.partie_interessee_id);
      downloadBlob(response.data, fileName(row.file));
    } catch (error) {
      setErrors((current) => ({ ...current, submit: error.message }));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog fullWidth maxWidth="lg" onClose={() => !saving && onClose()} open={open} scroll="paper">
      <DialogTitle sx={{ color: OUTIL_COLORS.navy, fontFamily: "Georgia, serif", fontSize: 17, fontWeight: 700 }}>
        Parties intéressées — {isCreate ? "Nouvelle fiche" : `Fiche #${row?.partie_interessee_id || ""}`}
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#f8fafc", display: "grid", gap: 1.5, p: { xs: 1.25, sm: 2 } }}>
        {errors.submit && <Alert severity="error">{errors.submit}</Alert>}
        <Box sx={{ bgcolor: "#edf3f8", border: `1px solid ${OUTIL_COLORS.border}`, borderRadius: 1, px: 1.5, py: 1 }}>
          <Typography color={OUTIL_COLORS.textMuted} fontSize={10.5}>Processus : <strong>{processus?.instance || "—"}</strong> · Fiche documentaire : <strong>{outilEntree?.outil_code || "—"}</strong></Typography>
        </Box>

        <Section title="PIÈCE JOINTE">
          <Box sx={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 1 }}>
            <input hidden onChange={selectFile} ref={inputRef} type="file" />
            {!isView && <Button disabled={saving} onClick={() => inputRef.current?.click()} size="small" startIcon={<AttachFileOutlinedIcon />} variant="outlined">{row?.file ? "Remplacer" : "Ajouter une pièce jointe"}</Button>}
            {file && <Typography fontSize={10.5}>{file.name}</Typography>}
            {!file && row?.file && <Button disabled={downloading} onClick={download} size="small" startIcon={downloading ? <CircularProgress size={14} /> : <DownloadOutlinedIcon />}>{fileName(row.file)}</Button>}
          </Box>
          {errors.file && <Typography color="error" fontSize={10}>{errors.file}</Typography>}
        </Section>

        <Section title="IDENTIFICATION DE LA PARTIE INTÉRESSÉE">
          <Grid container spacing={1.5}>
            <Grid item xs={12}><TextField disabled={isView || saving} error={Boolean(errors.nom_partie)} fullWidth helperText={errors.nom_partie} inputProps={{ maxLength: 250 }} label="Nom / Partie intéressée *" onChange={change("nom_partie")} size="small" sx={fieldSx} value={form.nom_partie} /></Grid>
            <Grid item xs={12}><TextField disabled={isView || saving} fullWidth label="Attentes & exigences" multiline minRows={3} onChange={change("attente")} size="small" sx={fieldSx} value={form.attente} /></Grid>
          </Grid>
        </Section>

        <Section title="ANALYSE DES RISQUES & OPPORTUNITÉS">
          <Grid container spacing={1.5}>
            <Grid item md={6} xs={12}><TextField disabled={isView || saving} fullWidth label="Risques identifiés" multiline minRows={3} onChange={change("risque")} size="small" sx={fieldSx} value={form.risque} /></Grid>
            <Grid item md={6} xs={12}><TextField disabled={isView || saving} fullWidth label="Opportunités identifiées" multiline minRows={3} onChange={change("opportunite")} size="small" sx={fieldSx} value={form.opportunite} /></Grid>
            <Grid item md={6} xs={12}><TextField disabled={isView || saving} fullWidth label="Objectifs qualité liés" multiline minRows={3} onChange={change("objectif")} size="small" sx={fieldSx} value={form.objectif} /></Grid>
          </Grid>
        </Section>

        <Section title="PLAN D’ACTION">
          <Grid container spacing={1.5}>
            <Grid item xs={12}><TextField disabled={isView || saving} fullWidth label="Actions à entreprendre" multiline minRows={3} onChange={change("action")} size="small" sx={fieldSx} value={form.action} /></Grid>
            <Grid item xs={12}><TextField disabled={isView || saving} fullWidth label="Déroulement / Modalités" multiline minRows={3} onChange={change("deroulement")} size="small" sx={fieldSx} value={form.deroulement} /></Grid>
            <Grid item md={6} xs={12}><TextField disabled={isView || saving} fullWidth label="Processus chargé" onChange={change("processus")} size="small" sx={fieldSx} value={form.processus} /></Grid>
            <Grid item md={3} xs={12}><TextField disabled={isView || saving} fullWidth InputLabelProps={{ shrink: true }} label="Date début" onChange={change("date_debut")} size="small" sx={fieldSx} type="date" value={form.date_debut} /></Grid>
            <Grid item md={3} xs={12}><TextField disabled={isView || saving} error={Boolean(errors.date_fin)} fullWidth helperText={errors.date_fin} InputLabelProps={{ shrink: true }} label="Date fin prévue" onChange={change("date_fin")} size="small" sx={fieldSx} type="date" value={form.date_fin} /></Grid>
            <Grid item md={6} xs={12}><TextField disabled={isView || saving} error={Boolean(errors.suivi_oi3)} fullWidth helperText={errors.suivi_oi3} inputProps={{ maxLength: 50 }} label="Suivi OI3" onChange={change("suivi_oi3")} size="small" sx={fieldSx} value={form.suivi_oi3} /></Grid>
            <Grid item md={6} xs={12}><TextField disabled fullWidth label="Statut" size="small" sx={fieldSx} value={row?.statut || "Brouillon"} /></Grid>
          </Grid>
        </Section>
      </DialogContent>
      <DialogActions sx={{ gap: 0.75, px: 2, py: 1.25 }}>
        <Button disabled={saving} onClick={onClose}>{isView ? "Fermer" : "Annuler"}</Button>
        {!isView && <Button disabled={saving} onClick={() => submit("save")} startIcon={<SaveOutlinedIcon />} variant="outlined">Sauvegarder uniquement</Button>}
        {!isView && <Button disabled={saving || isValidated} onClick={() => submit("validate")} startIcon={<VerifiedOutlinedIcon />} variant="contained">{isValidated ? "Déjà validé" : "Valider & Sauvegarder"}</Button>}
      </DialogActions>
    </Dialog>
  );
};

export default PartiesInteresseesDialog;
